import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable,
} from '@dnd-kit/core';
import { Clock, AlertTriangle } from 'lucide-react';

import useAtsBoardApi from './config/board';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import A11yDialog from '../../components/ui/A11yDialog';
import TextInput from '../../components/ui/TextInput.jsx';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { useAuth } from '../../context/AuthContext';

// TEK KAYDIRMA KURALI — sayfanın gövdesi ASLA kaymaz. Dış kap `height: 100%` +
// `min-height: 0`; yalnız pano şeridi yatay, yalnız kolon listesi dikey kayar.
// `min-height: 0` atlanırsa flex çocuğu içeriği kadar büyür ve taşma SAYFAYA
// döner (repo kuralı: tek kaydırma satır listesidir).
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const BoardScroller = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 0 0 272px;
  width: 272px;
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ $over, theme }) => ($over ? theme.colors.primary.main : theme.colors.surface.border)};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
`;

const ColumnHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const ColumnDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color, theme }) => $color || theme.colors.primary.main};
`;

const ColumnName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColumnCount = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// TEK dikey kaydırma yüzeyi. `min-height: 0` olmadan kolon içeriği kadar uzar.
const CardList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColumnFooter = styled.div`
  flex-shrink: 0;
  padding: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-left: 3px solid ${({ $breached, theme }) => ($breached ? theme.colors.auxiliar.danger : 'transparent')};
  border-radius: 6px;
  padding: 8px 10px;
  cursor: grab;
  opacity: ${({ $busy }) => ($busy ? 0.5 : 1)};
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const CardName = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MetaFlag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.auxiliar.danger : 'inherit')};
  font-weight: ${({ $danger }) => ($danger ? 700 : 400)};
`;

const StatusChip = styled.span`
  padding: 1px 6px;
  border-radius: 8px;
  background: #FFF3CD;
  color: #856404;
  font-weight: 700;
`;

const EmptyColumn = styled.div`
  padding: 12px 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyBoard = styled.div`
  padding: 24px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Bir kartın aşamada geçirdiği saat — kolonun SLA'sıyla karşılaştırılır.
// `defaultSlaHours === 0` "SLA yok" demektir (PARTİ 3 / BP-30) ve ihlal ÜRETMEZ.
export const isSlaBreached = (stageEnteredAt, defaultSlaHours, nowMs) => {
  if (!stageEnteredAt || !defaultSlaHours || defaultSlaHours <= 0) return false;
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return false;
  return (nowMs - entered) / 3600000 > defaultSlaHours;
};

export const daysInStage = (stageEnteredAt, nowMs) => {
  if (!stageEnteredAt) return null;
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return null;
  return Math.max(0, Math.floor((nowMs - entered) / 86400000));
};

// Sürükle-bırak kararının SAF hali: hedef kolon yoksa ya da kart zaten o
// kolondaysa hiçbir çağrı yapılmaz (sunucu `alreadyOnStage` ile 409 dönerdi ve
// kullanıcı uydurma bir hata görürdü).
export const resolveDrop = (activeId, overId, cardStageId) => {
  if (!activeId || !overId) return null;
  if (cardStageId === overId) return null;
  return { applicationId: activeId, toStageId: overId };
};

// BP-26 — "Elendi" kolonuna bırakmak başvuruyu `disqualified` yapar; backend o
// geçişte gerekçeyi ZORUNLU kılar (ats.application.disqualifyReasonRequired).
// Gerekçesiz sürükleme 400 alıp kartı yerine döndürürdü — panoyu kullanan işe
// alımcı hatanın sebebini göremezdi. Kolon terminal + `disqualified` ise önce sorulur.
export const requiresDisqualifyReason = (column) =>
  !!column && column.isTerminal === true && column.stageType === 'disqualified';

function BoardCard({ card, column, busy, onOpen, nowMs, draggable }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    disabled: !draggable,
    data: { stageId: column.stageId },
  });
  const breached = isSlaBreached(card.stageEnteredAt, column.defaultSlaHours, nowMs);
  const days = daysInStage(card.stageEnteredAt, nowMs);
  return (
    <Card
      ref={setNodeRef}
      $busy={busy || isDragging}
      $breached={breached}
      data-role={`board-card-${card.id}`}
      onClick={() => onOpen(card.id)}
      {...listeners}
      {...attributes}
    >
      <CardName>{card.candidateFullName}</CardName>
      <CardMeta>
        {days != null && (
          <MetaFlag $danger={breached}>
            {breached ? <AlertTriangle size={11} /> : <Clock size={11} />}
            {t('ats.application.board.daysInStage', { count: days })}
          </MetaFlag>
        )}
        {card.scoreAggregate != null && <span>{Number(card.scoreAggregate).toFixed(2)}</span>}
        {card.status !== 'active' && (
          <StatusChip>{t(`ats.application.status.${card.status}`, { defaultValue: card.status })}</StatusChip>
        )}
      </CardMeta>
    </Card>
  );
}

function BoardColumn({ column, busyCardId, onOpen, onLoadMore, nowMs, draggable }) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: column.stageId });
  const hasMore = column.cards.length < column.totalCount;
  return (
    <Column ref={setNodeRef} $over={isOver} data-role={`board-column-${column.stageId}`}>
      <ColumnHeader>
        <ColumnDot $color={column.color} />
        <ColumnName title={column.stageName}>{column.stageName}</ColumnName>
        <ColumnCount data-role={`board-count-${column.stageId}`}>{column.totalCount}</ColumnCount>
      </ColumnHeader>
      <CardList>
        {column.cards.length === 0 && <EmptyColumn>{t('ats.application.board.emptyColumn')}</EmptyColumn>}
        {column.cards.map((card) => (
          <BoardCard
            key={card.id}
            card={card}
            column={column}
            busy={busyCardId === card.id}
            onOpen={onOpen}
            nowMs={nowMs}
            draggable={draggable}
          />
        ))}
      </CardList>
      {hasMore && (
        <ColumnFooter>
          <Button
            variant="outline"
            data-role={`board-load-more-${column.stageId}`}
            onClick={() => onLoadMore(column.stageId)}
          >
            {t('ats.application.board.loadMore', { loaded: column.cards.length, total: column.totalCount })}
          </Button>
        </ColumnFooter>
      )}
    </Column>
  );
}

const ReasonModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const ReasonCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;

const ReasonTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReasonActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export default function PipelineBoardPage() {
  const { t } = useTranslation();
  const { id: jobPostingId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;

  const { fetching, loadFailed, board, busyCardId, loadMore, moveCard, refresh } = useAtsBoardApi(jobPostingId);
  // Zaman, render başına BİR kez okunur: her kartın kendi `Date.now()`'unu çağırması
  // aynı panoda tutarsız "gün" değerleri üretirdi.
  const [nowMs] = useState(() => Date.now());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const openApplication = useCallback((applicationId) => {
    history.push(`/ats/applications/${applicationId}`);
  }, [history]);

  const title = board?.jobPostingTitle || location.state?.postingTitle || '';
  const columns = useMemo(() => board?.columns || [], [board]);

  // Eleme gerekçesi istemi (BP-26): hedef kolon terminal-`disqualified` ise
  // taşıma önce gerekçe sorar, sonra çağrılır.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [reasonNote, setReasonNote] = useState('');

  const handleDragEnd = useCallback((event) => {
    const activeId = event?.active?.id;
    const overId = event?.over?.id;
    const cardStageId = event?.active?.data?.current?.stageId;
    const drop = resolveDrop(activeId, overId, cardStageId);
    if (!drop) return;
    const target = columns.find((c) => c.stageId === drop.toStageId);
    if (requiresDisqualifyReason(target)) {
      setReasonNote('');
      setReasonPrompt({ ...drop, stageName: target.stageName });
      return;
    }
    moveCard(drop.applicationId, drop.toStageId);
  }, [moveCard, columns]);

  // ESC, Vazgeç düğmesi ve atma onayı AYNI kapatma yolunu kullanır (FE-25).
  const closeReasonPrompt = useCallback(() => { setReasonPrompt(null); setReasonNote(''); }, []);

  const confirmDisqualifyMove = useCallback(async () => {
    if (!reasonPrompt || !reasonNote.trim()) return;
    const pending = reasonPrompt;
    // Diyalog API hatasında KAPANMAZ: yazılan gerekçe kaybolmamalı (FE-31 kuralı).
    const ok = await moveCard(pending.applicationId, pending.toStageId, reasonNote.trim());
    if (ok) {
      setReasonPrompt(null);
      setReasonNote('');
    }
  }, [reasonPrompt, reasonNote, moveCard]);

  return (
    <PageContainer>
      <Header>
        <Breadcrumbs items={[
          { label: t('ats.jobPosting.pageTitle'), path: '/ats/job-postings' },
          { label: title || '...' },
          { label: t('ats.application.board.title') },
        ]} />
        <HeaderRow>
          <Title>{t('ats.application.board.title')}</Title>
          <HeaderActions>
            <Button
              variant="outline"
              data-role="board-open-list"
              onClick={() => history.push({
                pathname: '/ats/applications',
                search: `?jobPostingId=${jobPostingId}`,
                state: { postingFilter: { id: jobPostingId, title } },
              })}
            >
              {t('ats.application.board.openList')}
            </Button>
            <Button variant="outline" data-role="board-refresh" onClick={refresh}>
              {t('common.refresh')}
            </Button>
          </HeaderActions>
        </HeaderRow>
        {loadFailed && <LoadErrorBanner message={t('ats.application.board.loadFailed')} />}
      </Header>

      {!loadFailed && !fetching && columns.length === 0 && (
        <EmptyBoard>{t('ats.application.board.noStages')}</EmptyBoard>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <BoardScroller data-role="board-scroller">
          {columns.map((column) => (
            <BoardColumn
              key={column.stageId}
              column={column}
              busyCardId={busyCardId}
              onOpen={openApplication}
              onLoadMore={loadMore}
              nowMs={nowMs}
              draggable={writable}
            />
          ))}
        </BoardScroller>
      </DndContext>

      <A11yDialog
        open={!!reasonPrompt}
        onClose={closeReasonPrompt}
        dirty={!!reasonNote.trim()}
        overlay={ReasonModal}
        card={ReasonCard}
        overlayProps={{ $open: true, 'data-role': 'board-disqualify-reason' }}
      >
        {({ titleId }) => (
          <>
            <ReasonTitle id={titleId}>{reasonPrompt.stageName}</ReasonTitle>
            <TextInput
              label={t('ats.application.reason')}
              type="textarea"
              rows={3}
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              required
            />
            <ReasonActions>
              <Button
                variant="outline"
                data-role="board-disqualify-cancel"
                onClick={closeReasonPrompt}
              >
                {t('common.cancel')}
              </Button>
              <Button
                data-role="board-disqualify-confirm"
                onClick={confirmDisqualifyMove}
                disabled={!reasonNote.trim() || busyCardId === reasonPrompt.applicationId}
              >
                {t('common.confirm')}
              </Button>
            </ReasonActions>
          </>
        )}
      </A11yDialog>
    </PageContainer>
  );
}
